import { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Nexoriq Analytics panel — funnel + sparklines + breakdown tables.
 * Props: data = /api/admin/analytics response
 */
export default function AnalyticsPanel({ data, loading }) {
  if (loading && !data) {
    return (
      <div
        className="font-mono text-sm uppercase tracking-[0.2em] text-nx-steel2"
        data-testid="analytics-loading"
      >
        → Loading analytics…
      </div>
    );
  }
  if (!data) return null;

  const { funnel = [], daily_views = [], daily_submits = [], event_breakdown = {}, top_pages = [], top_referrers = [], category_mix = [], totals = {} } = data;

  return (
    <div className="space-y-14" data-testid="analytics-panel">
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        <Stat label="Events (7d)" value={totals.events ?? 0} testid="analytics-total-events" />
        <Stat label="Unique sessions" value={totals.sessions ?? 0} testid="analytics-total-sessions" />
        <Stat label="RFQs (7d)" value={totals.rfqs ?? 0} testid="analytics-total-rfqs" />
        <Stat
          label="Overall conversion"
          value={(() => {
            const submits = funnel?.[2]?.sessions ?? 0;
            const views = funnel?.[0]?.sessions ?? 0;
            return views ? `${((submits / views) * 100).toFixed(1)}%` : "—";
          })()}
          small
          testid="analytics-total-cvr"
        />
      </div>

      {/* Funnel */}
      <section>
        <SectionHeading title="Funnel · 7-day rolling" tag="Views → Engaged → Submitted" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nx-navy/25 border border-nx-navy/25">
          {funnel.map((step, i) => (
            <FunnelStep key={step.stage} step={step} index={i} maxValue={funnel[0]?.value || 1} />
          ))}
        </div>
      </section>

      {/* Sparklines */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <TrendCard title="Daily page views" data={daily_views} testid="analytics-trend-views" accent="navy" />
        <TrendCard title="Daily RFQ submissions" data={daily_submits} testid="analytics-trend-submits" accent="steel" />
      </section>

      {/* Breakdowns */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <BreakdownTable
          title="Event mix"
          testid="analytics-events"
          rows={Object.entries(event_breakdown).map(([k, v]) => ({
            label: k.replace(/_/g, " "),
            count: v,
          })).sort((a, b) => b.count - a.count)}
        />
        <BreakdownTable
          title="Top pages"
          testid="analytics-top-pages"
          rows={top_pages.map((p) => ({ label: p.path, count: p.count }))}
          emptyLabel="No page views yet."
        />
        <BreakdownTable
          title="RFQ category mix"
          testid="analytics-category-mix"
          rows={category_mix.map((c) => ({ label: c.category, count: c.count }))}
          emptyLabel="No RFQ categories yet."
        />
      </section>

      {top_referrers.length > 0 && (
        <section>
          <SectionHeading title="Top external referrers" tag="Where traffic originated" />
          <BreakdownTable
            testid="analytics-referrers"
            rows={top_referrers.map((r) => ({ label: r.source, count: r.count }))}
          />
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, small, testid }) {
  return (
    <div className="border-t border-nx-navy pt-3" data-testid={testid}>
      <div
        className={
          "font-cabinet font-black text-nx-navy tracking-[-0.03em] leading-none " +
          (small ? "text-2xl md:text-3xl" : "text-3xl md:text-5xl")
        }
      >
        {value}
      </div>
      <div className="mt-2 font-mono text-[0.62rem] uppercase tracking-[0.18em] text-nx-steel2">
        {label}
      </div>
    </div>
  );
}

function SectionHeading({ title, tag }) {
  return (
    <div className="flex items-baseline justify-between mb-5 border-b border-nx-navy/25 pb-3">
      <h3 className="font-cabinet font-black text-xl md:text-2xl tracking-tight text-nx-navy">
        {title}
      </h3>
      {tag && (
        <div className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-nx-steel2">
          {tag}
        </div>
      )}
    </div>
  );
}

function FunnelStep({ step, index, maxValue }) {
  const widthPct = maxValue > 0 ? Math.max(6, (step.value / maxValue) * 100) : 6;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      className="bg-nx-bg p-6 md:p-8"
      data-testid={`funnel-step-${index}`}
    >
      <div className="flex items-baseline justify-between mb-6">
        <span className="font-mono text-[0.62rem] uppercase tracking-[0.24em] text-nx-steel2">
          Step 0{index + 1}
        </span>
        {step.conversion !== null && step.conversion !== undefined && (
          <span
            className="font-mono text-[0.68rem] uppercase tracking-[0.14em] text-nx-navy border border-nx-navy px-2 py-0.5"
            data-testid={`funnel-cvr-${index}`}
          >
            {step.conversion.toFixed(1)}% CVR
          </span>
        )}
      </div>
      <div className="font-cabinet font-black text-nx-ink text-5xl md:text-6xl tracking-[-0.04em] leading-none">
        {step.value}
      </div>
      <div className="mt-2 text-nx-navy2 text-sm">{step.stage}</div>
      {step.total_rfqs !== undefined && step.total_rfqs !== step.value && (
        <div className="mt-1 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-nx-steel2">
          {step.total_rfqs} total incl. legacy
        </div>
      )}
      <div className="mt-5 h-1.5 bg-nx-bg2 relative overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${widthPct}%` }}
          transition={{ duration: 0.9, delay: 0.1 + index * 0.1 }}
          className="absolute inset-y-0 left-0 bg-nx-navy"
        />
      </div>
    </motion.div>
  );
}

function TrendCard({ title, data, testid, accent = "navy" }) {
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.count)), [data]);
  const total = data.reduce((s, d) => s + d.count, 0);
  const barColor = accent === "steel" ? "bg-nx-steel" : "bg-nx-navy";
  return (
    <div className="border border-nx-navy p-6 md:p-8" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-6">
        <h3 className="font-cabinet font-black text-lg md:text-xl tracking-tight text-nx-navy">
          {title}
        </h3>
        <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-nx-steel2">
          Total {total}
        </span>
      </div>
      <div className="flex items-end gap-2 h-32">
        {data.map((d, i) => {
          const h = Math.max(2, (d.count / max) * 100);
          return (
            <div
              key={d.date}
              className="flex-1 flex flex-col items-center justify-end gap-2 group"
              data-testid={`${testid}-bar-${i}`}
              title={`${d.date} — ${d.count}`}
            >
              <div className="text-[0.6rem] font-mono text-nx-navy opacity-0 group-hover:opacity-100 transition-opacity">
                {d.count}
              </div>
              <div className={`${barColor} w-full`} style={{ height: `${h}%` }} />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[0.55rem] font-mono uppercase tracking-[0.14em] text-nx-steel2">
        {data.map((d) => (
          <span key={d.date}>{d.date.slice(5)}</span>
        ))}
      </div>
    </div>
  );
}

function BreakdownTable({ title, rows = [], emptyLabel = "No data yet.", testid }) {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div data-testid={testid}>
      {title && <SectionHeading title={title} />}
      {rows.length === 0 ? (
        <div className="text-nx-steel2 text-sm font-mono uppercase tracking-[0.14em]">
          {emptyLabel}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const pct = (r.count / total) * 100;
            return (
              <li key={r.label} className="border-b border-nx-navy/15 pb-2">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-nx-navy truncate pr-2">{r.label}</span>
                  <span className="font-mono text-xs text-nx-navy2">
                    {r.count} · {pct.toFixed(0)}%
                  </span>
                </div>
                <div className="mt-1.5 h-0.5 bg-nx-bg2 relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-nx-navy"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
